/**
 * Agenda date/time helpers. No third-party calendar libs.
 * Prefer browser IANA timezone; fallback America/Guatemala.
 */

export const FALLBACK_TIMEZONE = "America/Guatemala";

export function resolveBrowserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === "string" && tz.trim()) return tz.trim();
  } catch {
    /* ignore */
  }
  return FALLBACK_TIMEZONE;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** Local calendar Y-M-D parts for a Date in a given IANA zone. */
export function getZonedParts(date, timeZone = resolveBrowserTimezone()) {
  const d = date instanceof Date ? date : new Date(date);
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(d).filter((p) => p.type !== "literal").map((p) => [p.type, p.value])
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second || 0),
  };
}

/**
 * Find the UTC Instant whose wall-clock in `timeZone` matches local Y-M-D H:M.
 */
export function zonedLocalToUtcDate({ year, month, day, hour = 0, minute = 0, second = 0 }, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  let instant = new Date(utcGuess);
  for (let i = 0; i < 3; i += 1) {
    const asZoned = getZonedParts(instant, timeZone);
    const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    const actualAsUtc = Date.UTC(
      asZoned.year,
      asZoned.month - 1,
      asZoned.day,
      asZoned.hour,
      asZoned.minute,
      asZoned.second
    );
    const delta = desiredAsUtc - actualAsUtc;
    if (delta === 0) break;
    instant = new Date(instant.getTime() + delta);
  }
  return instant;
}

export function toUtcIso(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Local form date (YYYY-MM-DD) + time (HH:mm) → ISO UTC string. */
export function localFormToUtcIso(dateStr, timeStr, timeZone = resolveBrowserTimezone()) {
  const dateMatch = String(dateStr || "").trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(timeStr || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!dateMatch || !timeMatch) {
    throw new Error("Fecha u hora no válida");
  }
  const instant = zonedLocalToUtcDate(
    {
      year: Number(dateMatch[1]),
      month: Number(dateMatch[2]),
      day: Number(dateMatch[3]),
      hour: Number(timeMatch[1]),
      minute: Number(timeMatch[2]),
      second: 0,
    },
    timeZone
  );
  return toUtcIso(instant);
}

/**
 * Inclusive local calendar day → [startUtcIso, nextDayStartUtcIso)
 * for GET /appointments?start=&end=
 */
export function getLocalDayRangeUtc(dateInput, timeZone = resolveBrowserTimezone()) {
  let year;
  let month;
  let day;
  if (typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) {
    const [y, m, d] = dateInput.trim().split("-").map(Number);
    year = y;
    month = m;
    day = d;
  } else {
    const parts = getZonedParts(dateInput || new Date(), timeZone);
    year = parts.year;
    month = parts.month;
    day = parts.day;
  }
  const start = zonedLocalToUtcDate({ year, month, day, hour: 0, minute: 0, second: 0 }, timeZone);
  const nextDay = new Date(Date.UTC(year, month - 1, day) + 24 * 60 * 60 * 1000);
  const end = zonedLocalToUtcDate(
    {
      year: nextDay.getUTCFullYear(),
      month: nextDay.getUTCMonth() + 1,
      day: nextDay.getUTCDate(),
      hour: 0,
      minute: 0,
      second: 0,
    },
    timeZone
  );
  return { start: toUtcIso(start), end: toUtcIso(end), localDate: `${year}-${pad2(month)}-${pad2(day)}` };
}

export function formatAppointmentTime(value, locale = "es-GT", timeZone = resolveBrowserTimezone()) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(d);
}

export function formatAgendaDate(value, locale = "es-GT", timeZone = resolveBrowserTimezone()) {
  if (!value) return "";
  let d;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, day] = value.trim().split("-").map(Number);
    d = zonedLocalToUtcDate({ year: y, month: m, day, hour: 12, minute: 0 }, timeZone);
  } else {
    d = value instanceof Date ? value : new Date(value);
  }
  if (Number.isNaN(d.getTime())) return "";
  const raw = new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
  return raw.charAt(0).toLocaleUpperCase(locale) + raw.slice(1);
}

export function getAppointmentDurationMinutes(start, end) {
  const a = start instanceof Date ? start : new Date(start);
  const b = end instanceof Date ? end : new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 60000));
}

export function shiftLocalDateString(dateStr, deltaDays) {
  const match = String(dateStr || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return dateStr;
  const base = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const next = new Date(base + deltaDays * 24 * 60 * 60 * 1000);
  return `${next.getUTCFullYear()}-${pad2(next.getUTCMonth() + 1)}-${pad2(next.getUTCDate())}`;
}

export function todayLocalDateString(timeZone = resolveBrowserTimezone()) {
  const p = getZonedParts(new Date(), timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

export function utcIsoToLocalDateAndTime(iso, timeZone = resolveBrowserTimezone()) {
  const d = new Date(iso);
  const p = getZonedParts(d, timeZone);
  return {
    date: `${p.year}-${pad2(p.month)}-${pad2(p.day)}`,
    time: `${pad2(p.hour)}:${pad2(p.minute)}`,
  };
}
